import React, { useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga';
import Helmet from 'react-helmet';
import Input from '../../../../components/new-dashboard/custom-input';
import { singleShipmentLinks } from '../../../../constants/shipment-links';
import { useRecoilState } from 'recoil';
import { useHistory } from 'react-router-dom';
import { AddCircle, RemoveCircle } from '@mui/icons-material';
import { Alert, Button, Grid, Box } from '@mui/material';
import { saveShipmentDetailsState } from '../../../../recoil/atoms';
import { itemCategories } from '../../../../constants/item-categories';
import SubmitButton from '../../../../components/submit-button';
import numberWithCommas from '../../../../utilities/number-with-commas';
import NewShippingStepsLayout from '../../../../components/Layouts/new-shipping-steps-layout';
import { useFormik } from 'formik';
import { checkIsCompleted } from '../../helper-functions';
import * as yup from 'yup';
import StickyNoteAlert from '../../../../components/Commons/sticky-note';
import VolumetricWeight from '../../../../components/volumetric-weight-switch';
import { InfoYellowIcon } from '../../../../constants/asset-contants';
import CustomAutoComplete from '../../../../components/new-dashboard/custom-autocomplete';
import {
  getItemCategoryMessage,
  itemCategoryRules
} from '../../../../constants/item-category-dictionary';

const init = [
  {
    category: '',
    weight: '',
    width: '',
    height: '',
    length: '',
    quantity: '',
    value: '',
    description: '',
    isVolumetric: false
  }
];
const ItemDescription = () => {
  const history = useHistory();
  const [initialValues, setInitialValues] = useState(init);
  const [shipmentDetail, setSaveShipmentDetail] = useRecoilState(
    saveShipmentDetailsState
  );
  const currentIndex = singleShipmentLinks.findIndex(
    link => history.location.pathname === link.link
  );
  const handleIsVolumetricChange = (fieldName, newValue) => {
    setFieldValue(fieldName, newValue);
  };

  const validationSchema = yup.object().shape({
    items: yup.array().of(
      yup.object().shape({
        category: yup.string().required('Please provide a Category'),
        isVolumetric: yup.bool(),
        weight: yup.number().when(['isVolumetric'], {
          is: isVolumetric => !isVolumetric,
          then: yup
            .number()
            .min(0.09999, 'Please enter a weight greater than zero (0)')
            .required('Please enter the weight of your item(s)'),
          otherwise: yup.number().nullable().notRequired()
        }),
        width: yup.number().when(['isVolumetric'], {
          is: isVolumetric => isVolumetric === true,

          then: yup
            .number()
            .min(0.09999, 'Please enter a width greater than zero (0)')
            .required('Please enter the width of your box'),
          otherwise: yup.number().nullable().notRequired()
        }),
        height: yup.number().when(['isVolumetric'], {
          is: isVolumetric => isVolumetric === true,

          then: yup
            .number()
            .min(0.09999, 'Please enter a height greater than zero (0)')
            .required('Please enter the height of your box'),
          otherwise: yup.number().nullable().notRequired()
        }),
        length: yup.number().when(['isVolumetric'], {
          is: isVolumetric => isVolumetric === true,

          then: yup
            .number()
            .min(0.09999, 'Please enter a length greater than zero (0)')
            .required('Please enter the length of your box'),
          otherwise: yup.number().nullable().notRequired()
        }),
        quantity: yup.number().required('Please provide the quantity'),
        value: yup
          .number()
          .moreThan(0, 'Please enter a number above 1')
          .required('Please provide the value'),
        description: yup
          .string()
          .required('Please provide the item description')
      })
    )
  });

  const {
    values,
    errors,
    validateForm,
    setFieldValue,
    handleChange: handleFormikChange,
    handleSubmit
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      items: initialValues
    },
    validationSchema,
    onSubmit: values => {
      const newItems = values.items.map(item => {
        let weight = item.weight;
        if (item.isVolumetric) {
          weight = Number(
            ((item.width * item.height * item.length) / 5000).toFixed(2)
          );
        }

        return {
          ...item,
          weight,
          value: item.value * 100,
          isCompleted: true
        };
      });
      if (newItems.length === values.items.length) {
        setSaveShipmentDetail({
          ...shipmentDetail,
          items: newItems
        });
        history.push(singleShipmentLinks[currentIndex + 1].link);
      }
    }
  });

  const handleChange = ({ target: { name, value } }) => {
    const itemValue = name.match(new RegExp('quantity|weight', 'i'))
      ? value
        ? parseFloat(value)
        : ''
      : value;
    handleFormikChange({ target: { name, value: itemValue } });
  };

  const insertForm = () => {
    const allItems = [...values.items];
    if (allItems.length < 6) {
      allItems.push({
        category: '',
        weight: '',
        quantity: '',
        value: '',
        description: '',
        height: '',
        length: '',
        width: '',
        isVolumetric: false
      });
      setInitialValues(allItems);
    }
  };

  const disabled = useMemo(() => {
    return values.items.some(
      item =>
        itemCategoryRules[item.category] &&
        !itemCategoryRules[item.category]?.canShip
    );
  }, [values.items]);

  const deleteItem = deleteIndex => {
    if (values.items.length > 1) {
      const allItems = [...values.items];
      setInitialValues(allItems.filter((_, index) => index !== deleteIndex));
    }
  };

  const handleValidations = async values => {
    const errors = await validateForm(values);
    return !Boolean(errors);
  };

  useEffect(() => {
    ReactGA.event({
      category: 'Shipment',
      action: 'User completes receivers information'
    });
    if (shipmentDetail?.items?.length) {
      const items = shipmentDetail?.items?.map(item => ({
        ...item,
        value: item?.value / 100
      }));

      setInitialValues([...items]);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (history.location.state?.reValidate) {
      const isCompleted = checkIsCompleted(currentIndex, shipmentDetail);
      if (!isCompleted) {
        handleValidations(values);
      }
    }
    history.replace({
      state: {}
    });
    // eslint-disable-next-line
  }, [history.location.state?.reValidate]);

  return (
    <NewShippingStepsLayout>
      <Helmet>
        <meta
          name='description'
          content='Topship Africa Shipment Request Item Description'
        />
        <title>Topship Africa Shipment Request Item Description</title>
        <link
          rel='canonical'
          href='https://topship.africa/shipment-request-item-description'
        />
      </Helmet>
      <form onSubmit={handleSubmit} className='item-description'>
        <label className='item-description__label'>
          Enter details about the item you want to ship
        </label>
        <div className='item-description__mains'>
          {values.items.map((item, index) => {
            const message = getItemCategoryMessage(
              item.category,
              shipmentDetail
            );

            return (
              <div key={index} className='item-description__main'>
                <div className='item-description__main__wrap'>
                  <div className='item-description__main__action'>
                    {values.items.length > 1 && (
                      <Button variant='text' onClick={() => deleteItem(index)}>
                        <span>Remove item</span>
                        <RemoveCircle />
                      </Button>
                    )}
                  </div>
                  <div className='item-description__main__grid'>
                    <a
                      href={'https://topship.africa/prohibited-items'}
                      target='_blank'
                      rel='noreferrer'
                      style={{
                        textDecoration: 'none',
                        transform: 'none'
                      }}
                      className='item-description__main__grid__helper category-helper'
                    >
                      SEE LIST OF PROHIBITED & RESTRICTED ITEMS.
                    </a>
                    <Grid
                      container
                      columnSpacing='10px'
                      rowSpacing='16px'
                      className='item-description__main__grid__form'
                    >
                      <CustomAutoComplete
                        options={itemCategories}
                        getOptionLabel={option =>
                          option ? option?.category : ''
                        }
                        isOptionEqualToValue={(option, value) =>
                          option?.value === value?.value
                        }
                        renderOption={(props, option) => (
                          <Box key={props.id} component='li' {...props}>
                            {option?.category}
                          </Box>
                        )}
                        value={
                          itemCategories.find(
                            category => category.value === item.category
                          ) || { category: '', value: '' }
                        }
                        noOptionsText={'Item Category not found'}
                        onChange={(event, value, reason) => {
                          if (reason === 'clear') {
                            setFieldValue(`items.${index}.category`, '');
                          } else {
                            setFieldValue(
                              `items.${index}.category`,
                              value?.value
                            );
                          }
                        }}
                        textfieldProps={{
                          lg: 6,
                          md: 6,
                          name: `items.${index}.category`,
                          customLabel: 'Select Item Category',
                          containerClassName: 'item-description__main__input',
                          value: item.category,
                          error: Boolean(errors.items?.[index]?.category),
                          helperText: errors.items?.[index]?.category,
                          onChange: event => {
                            return handleChange(event);
                          }
                        }}
                      />
                      <div className='mobile-alert'>
                        {itemCategoryRules[item.category] && message && (
                          <Alert
                            severity={'info'}
                            className='item-description__main__alert'
                            sx={{
                              fontSize: '1.5rem',
                              fontFamily: 'General Sans',
                              marginTop: '10px',
                              backgroundColor: '#e6f2fe',
                              color: '#22428F',
                              border: 'none',
                              '& .MuiAlert-icon': {
                                color: '#22428F'
                              }
                            }}
                          >
                            {message}
                          </Alert>
                        )}
                      </div>
                      <Input
                        lg={6}
                        md={12}
                        name={`items.${index}.value`}
                        placeholder='50,000.00'
                        customLabel='Item Value (₦)'
                        value={`${numberWithCommas(item?.value || 0)}`}
                        onChange={event => {
                          const { value, name } = event.target;
                          if (!(value && value.length)) {
                            handleChange({ target: { name, value: '' } });
                          } else if (!isNaN(value.split(',').join(''))) {
                            const itemValue = parseInt(
                              value.split(',').join(''),
                              10
                            );
                            handleChange({
                              target: { name, value: itemValue }
                            });
                          }
                        }}
                        containerClassName='item-description__main__input'
                        error={Boolean(errors.items?.[index]?.value)}
                        helperText={errors.items?.[index]?.value}
                      />
                    </Grid>
                    <div className='web-alert'>
                      {itemCategoryRules[item.category] && message && (
                        <Alert
                          severity={'info'}
                          className='item-description__main__alert'
                          sx={{
                            fontSize: '1.5rem',
                            fontFamily: 'General Sans',
                            marginTop: '10px',
                            backgroundColor: '#e6f2fe',
                            color: '#22428F',
                            border: 'none',
                            '& .MuiAlert-icon': {
                              color: '#22428F'
                            }
                          }}
                        >
                          {message}
                        </Alert>
                      )}
                    </div>

                    <a
                      href={
                        'https://www.docdroid.net/AX2ViKa/labelling-a-package-for-shipping-pdf'
                      }
                      target='_blank'
                      rel='noreferrer'
                      style={{
                        textDecoration: 'none',
                        transform: 'none'
                      }}
                      className='item-description__main__grid__helper'
                    >
                      SEE HOW TO LABEL YOUR PACKAGE.
                    </a>
                    <Grid container>
                      <Input
                        fullWidth
                        multiline
                        minRows={4}
                        name={`items.${index}.description`}
                        value={item.description}
                        onChange={e => handleChange(e)}
                        customLabel='Detailed Item Description'
                        containerClassName='item-description__main__input'
                        error={Boolean(errors.items?.[index]?.description)}
                        helperText={errors.items?.[index]?.description}
                        placeholder='A piece of text that clearly describes the item being packaged for shipping, it should leave no room for guesses so that TopShip can know how best to handle it'
                      />
                    </Grid>
                    <div className=''>
                      <VolumetricWeight
                        checkBoxName={`items.${index}.isVolumetric`}
                        isActive={item.isVolumetric}
                        handleToggleSwitch={() =>
                          handleIsVolumetricChange(
                            `items.${index}.isVolumetric`,
                            !item.isVolumetric
                          )
                        }
                      />
                    </div>
                    {item.isVolumetric ? (
                      <div
                        style={{
                          marginTop: '30px'
                        }}
                      >
                        <Grid
                          container
                          columnSpacing='10px'
                          rowSpacing='16px'
                          className='item-description__main__grid__form'
                        >
                          <Input
                            lg={6}
                            md={12}
                            type='number'
                            name={`items.${index}.quantity`}
                            placeholder='20'
                            value={item.quantity}
                            onChange={e => {
                              if (isNaN(e.target.value)) {
                                return handleChange({
                                  target: {
                                    name: e.target.name,
                                    value: ''
                                  }
                                });
                              } else {
                                handleChange(e);
                              }
                            }}
                            customLabel='Quantity'
                            error={Boolean(errors.items?.[index]?.quantity)}
                            helperText={errors.items?.[index]?.quantity}
                            containerClassName='item-description__main__input'
                            inputProps={{
                              min: 1
                            }}
                          />
                          <Input
                            lg={6}
                            md={12}
                            containerClassName='item-description__main__input'
                            type='number'
                            name={`items.${index}.width`}
                            customLabel='Width (CM)'
                            placeholder='Enter Width (CM)'
                            value={item.width}
                            onChange={e => {
                              if (isNaN(e.target.value)) {
                                return handleChange({
                                  target: {
                                    name: e.target.name,
                                    value: ''
                                  }
                                });
                              } else {
                                handleChange(e);
                              }
                            }}
                            error={Boolean(errors.items?.[index]?.width)}
                            helperText={errors.items?.[index]?.width}
                            inputProps={{
                              min: 1,
                              step: 'any'
                            }}
                          />
                        </Grid>{' '}
                        <Grid
                          container
                          columnSpacing='10px'
                          rowSpacing='16px'
                          className='item-description__main__grid__form mt-1'
                        >
                          <Input
                            lg={6}
                            md={12}
                            containerClassName='item-description__main__input'
                            type='number'
                            name={`items.${index}.length`}
                            customLabel='Length (CM)'
                            placeholder='Enter Length (CM)'
                            value={item.length}
                            onChange={e => {
                              if (isNaN(e.target.value)) {
                                return handleChange({
                                  target: {
                                    name: e.target.name,
                                    value: ''
                                  }
                                });
                              } else {
                                handleChange(e);
                              }
                            }}
                            error={Boolean(errors.items?.[index]?.length)}
                            helperText={errors.items?.[index]?.length}
                            inputProps={{
                              min: 1,
                              step: 'any'
                            }}
                          />
                          <Input
                            lg={6}
                            md={12}
                            containerClassName='item-description__main__input'
                            type='number'
                            name={`items.${index}.height`}
                            customLabel='Height (CM)'
                            placeholder='Enter Height'
                            value={item.height}
                            onChange={e => {
                              if (isNaN(e.target.value)) {
                                return handleChange({
                                  target: {
                                    name: e.target.name,
                                    value: ''
                                  }
                                });
                              } else {
                                handleChange(e);
                              }
                            }}
                            error={Boolean(errors.items?.[index]?.height)}
                            helperText={errors.items?.[index]?.height}
                            inputProps={{
                              min: 1,
                              step: 'any'
                            }}
                          />
                        </Grid>
                      </div>
                    ) : (
                      <>
                        <a
                          href={'https://docdro.id/khtz9i8'}
                          target='_blank'
                          rel='noreferrer'
                          style={{
                            textDecoration: 'none',
                            transform: 'none'
                          }}
                          className='item-description__main__grid__helper'
                        >
                          NEED HELP?
                        </a>

                        <Grid
                          container
                          columnSpacing='10px'
                          rowSpacing='16px'
                          className='item-description__main__grid__form'
                        >
                          <Input
                            lg={6}
                            md={12}
                            type='number'
                            name={`items.${index}.quantity`}
                            placeholder='20'
                            value={item.quantity}
                            onChange={e => {
                              if (isNaN(e.target.value)) {
                                return handleChange({
                                  target: {
                                    name: e.target.name,
                                    value: ''
                                  }
                                });
                              } else {
                                handleChange(e);
                              }
                            }}
                            customLabel='Quantity'
                            error={Boolean(errors.items?.[index]?.quantity)}
                            helperText={errors.items?.[index]?.quantity}
                            containerClassName='item-description__main__input'
                            inputProps={{
                              min: 1
                            }}
                          />
                          <Input
                            lg={6}
                            md={12}
                            type='number'
                            name={`items.${index}.weight`}
                            customLabel='Weight (KG)'
                            placeholder='Enter Weight'
                            value={item.weight}
                            onChange={e => {
                              if (isNaN(e.target.value)) {
                                return handleChange({
                                  target: {
                                    name: e.target.name,
                                    value: ''
                                  }
                                });
                              } else {
                                handleChange(e);
                              }
                            }}
                            error={Boolean(errors.items?.[index]?.weight)}
                            helperText={errors.items?.[index]?.weight}
                            containerClassName='item-description__main__input'
                            inputProps={{
                              min: 1,
                              step: 'any'
                            }}
                          />
                        </Grid>
                      </>
                    )}

                    <div className='mt-3' />

                    <StickyNoteAlert isVolumetric={true} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className='item-description__action'>
          <Button
            variant='text'
            disabled={values.items.length >= 5}
            onClick={insertForm}
          >
            <span>Add another item</span>
            <AddCircle />
          </Button>
        </div>
        <div className='mb-2'>
          <Alert
            severity={'info'}
            sx={{
              marginTop: '10px',
              fontSize: '1.5rem',
              fontWeight: 'normal',
              fontFamily: 'General Sans',
              backgroundColor: 'rgba(239, 165, 22, 0.1)',
              alignItems: 'flex-start',
              color: '#0F1414',
              border: 'none',
              '& .MuiAlert-icon': {
                fontSize: '2rem',
                marginRight: '5px'
              }
            }}
            icon={<img src={InfoYellowIcon} alt='info' />}
          >
            <b>Please be transparent.</b> Declaring a lower value than your item
            is worth could attract an additional fine from customs officials.
          </Alert>
        </div>
        <div className='item-description__footer'>
          <div
            onClick={() =>
              history.push(singleShipmentLinks[currentIndex].initialLink)
            }
            className='item-description__footer__action'
          >
            Go Back
          </div>
          <SubmitButton text='Continue' disabled={disabled} />
        </div>
      </form>
    </NewShippingStepsLayout>
  );
};

export default ItemDescription;
